import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiGlobe, FiImage } = FiIcons;

const DomainModal = ({ isOpen, onClose, onSave, domain = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    domain_authority: '',
    spam_score: '',
    niche: '',
    favicon_url: '',
    registration_date: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [faviconPreview, setFaviconPreview] = useState('');

  useEffect(() => {
    if (domain) {
      // Format registration date properly for the date input
      let formattedDate = '';
      if (domain.registration_date) {
        try {
          let date;
          
          // Handle different date formats
          if (domain.registration_date instanceof Date) {
            date = domain.registration_date;
          } else if (typeof domain.registration_date === 'string') {
            date = new Date(domain.registration_date);
          } else if (domain.registration_date.toDate && typeof domain.registration_date.toDate === 'function') {
            // Firebase Timestamp
            date = domain.registration_date.toDate();
          } else {
            date = new Date(domain.registration_date);
          }
          
          if (!isNaN(date.getTime())) {
            // Format as YYYY-MM-DD for HTML date input
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            formattedDate = `${year}-${month}-${day}`;
          }
        } catch (error) {
          console.warn('Invalid date format:', domain.registration_date);
          formattedDate = '';
        }
      }

      setFormData({
        name: domain.name || '',
        url: domain.url || '',
        domain_authority: domain.domain_authority ? domain.domain_authority.toString() : '',
        spam_score: domain.spam_score ? domain.spam_score.toString() : '',
        niche: domain.niche || '',
        favicon_url: domain.favicon_url || '',
        registration_date: formattedDate,
        notes: domain.notes || ''
      });
      setFaviconPreview(domain.favicon_url || '');
    } else {
      setFormData({
        name: '',
        url: '',
        domain_authority: '',
        spam_score: '',
        niche: '',
        favicon_url: '',
        registration_date: '',
        notes: ''
      });
      setFaviconPreview('');
    }
  }, [domain]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Update favicon preview
    if (name === 'favicon_url') {
      setFaviconPreview(value);
    }

    // Auto-generate favicon URL from domain URL
    if (name === 'url' && value && !formData.favicon_url) {
      try {
        const urlObj = new URL(value);
        const faviconUrl = `${urlObj.protocol}//${urlObj.hostname}/favicon.ico`;
        setFormData(prev => ({ ...prev, favicon_url: faviconUrl }));
        setFaviconPreview(faviconUrl);
      } catch (error) {
        // Invalid URL, ignore
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const processedData = {
        ...formData,
        domain_authority: formData.domain_authority ? parseInt(formData.domain_authority) : null,
        spam_score: formData.spam_score ? parseFloat(formData.spam_score) : 0,
        registration_date: formData.registration_date ? new Date(formData.registration_date) : null
      };

      await onSave(processedData);
      onClose();
    } catch (error) {
      console.error('Error saving domain:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSpamScoreColor = (score) => {
    if (score <= 10) return 'text-success-600';
    return 'text-danger-600';
  };

  const calculateDomainAge = () => {
    if (!formData.registration_date) return null;
    
    try {
      const regDate = new Date(formData.registration_date);
      const today = new Date();
      
      if (isNaN(regDate.getTime())) return null;
      
      const diffTime = Math.abs(today - regDate);
      const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365.25);
      
      if (diffYears < 1) {
        const diffMonths = Math.floor(diffYears * 12);
        return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
      } else {
        const years = Math.floor(diffYears);
        const months = Math.floor((diffYears % 1) * 12);
        if (months > 0) {
          return `${years} year${years !== 1 ? 's' : ''}, ${months} month${months !== 1 ? 's' : ''}`;
        } else {
          return `${years} year${years !== 1 ? 's' : ''}`;
        }
      }
    } catch (error) {
      console.warn('Error calculating domain age:', error);
      return null;
    }
  };

  const domainAge = calculateDomainAge();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {domain ? 'Edit Domain' : 'Add New Domain'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Domain Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Example Blog"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Domain URL *
            </label>
            <input
              type="url"
              name="url"
              value={formData.url}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="https://example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Registration Date
            </label>
            <input
              type="date"
              name="registration_date"
              value={formData.registration_date}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {domainAge && (
              <p className="text-sm text-primary-600 mt-1 font-medium">
                Domain age: {domainAge}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Domain Authority
              </label>
              <input
                type="number"
                name="domain_authority"
                value={formData.domain_authority}
                onChange={handleChange}
                min="1"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="50"
              />
              <p className="text-xs text-gray-500 mt-1">1-100 scale</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Spam Score
              </label>
              <input
                type="number"
                name="spam_score"
                value={formData.spam_score}
                onChange={handleChange}
                min="0"
                max="100"
                step="0.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="5.2"
              />
              <p className={`text-xs mt-1 ${getSpamScoreColor(parseFloat(formData.spam_score) || 0)}`}>
                {parseFloat(formData.spam_score) <= 10 ? '✓ Good score' : '⚠ High spam score'}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Niche *
            </label>
            <input
              type="text"
              name="niche"
              value={formData.niche}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Technology, Health, Finance, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Favicon URL
            </label>
            <div className="flex space-x-2">
              <input
                type="url"
                name="favicon_url"
                value={formData.favicon_url}
                onChange={handleChange}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="https://example.com/favicon.ico"
              />
              <div className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                {faviconPreview ? (
                  <img
                    src={faviconPreview}
                    alt="Favicon preview"
                    className="w-6 h-6 rounded-sm"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <SafeIcon
                  icon={faviconPreview ? FiGlobe : FiImage}
                  className="w-4 h-4 text-gray-400"
                  style={{ display: faviconPreview ? 'none' : 'flex' }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Auto-generated from domain URL, or enter custom favicon URL
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Additional notes about this domain..."
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : (domain ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default DomainModal;